import {
  CalendarDays,
  CheckSquare,
  FileSignature,
  PenLine,
  Type,
} from "lucide-react";

export type FieldType = "signature" | "initials" | "date" | "text" | "checkbox";

export type SignerSlot = {
  id: string;
  displayName?: string;
  kind: "contact" | "user";
  label: string;
  colorIndex?: number;
  signerOrder?: number;
  slotName: string;
};

export type PendingSignerSlot = {
  fieldCount: number;
  index: number;
  kind: SignerSlot["kind"];
};

export type LayoutField = {
  fontFamily?: "Times New Roman" | "Arial" | "Courier New";
  fontSize?: number;
  height: number;
  id: string;
  label: string;
  page: number;
  signerId: string;
  signerOrder?: number;
  type: FieldType;
  width: number;
  x: number;
  y: number;
};

export const SIGNER_COLORS = [
  {
    bg: "rgba(59,130,246,0.10)",
    border: "#3b82f6",
    selectedBg: "rgba(37,99,235,0.16)",
    selectedBorder: "#2563eb",
    text: "text-blue-800",
    pill: "bg-blue-100 text-blue-700",
  },
  {
    bg: "rgba(168,85,247,0.10)",
    border: "#a855f7",
    selectedBg: "rgba(147,51,234,0.16)",
    selectedBorder: "#9333ea",
    text: "text-purple-800",
    pill: "bg-purple-100 text-purple-700",
  },
  {
    bg: "rgba(34,197,94,0.10)",
    border: "#22c55e",
    selectedBg: "rgba(22,163,74,0.16)",
    selectedBorder: "#16a34a",
    text: "text-green-800",
    pill: "bg-green-100 text-green-700",
  },
  {
    bg: "rgba(249,115,22,0.10)",
    border: "#f97316",
    selectedBg: "rgba(234,88,12,0.16)",
    selectedBorder: "#ea580c",
    text: "text-orange-800",
    pill: "bg-orange-100 text-orange-700",
  },
  {
    bg: "rgba(244,63,94,0.10)",
    border: "#f43f5e",
    selectedBg: "rgba(225,29,72,0.16)",
    selectedBorder: "#e11d48",
    text: "text-rose-800",
    pill: "bg-rose-100 text-rose-700",
  },
  {
    bg: "rgba(6,182,212,0.10)",
    border: "#06b6d4",
    selectedBg: "rgba(8,145,178,0.16)",
    selectedBorder: "#0891b2",
    text: "text-cyan-800",
    pill: "bg-cyan-100 text-cyan-700",
  },
  {
    bg: "rgba(245,158,11,0.10)",
    border: "#f59e0b",
    selectedBg: "rgba(217,119,6,0.16)",
    selectedBorder: "#d97706",
    text: "text-amber-800",
    pill: "bg-amber-100 text-amber-700",
  },
  {
    bg: "rgba(99,102,241,0.10)",
    border: "#6366f1",
    selectedBg: "rgba(79,70,229,0.16)",
    selectedBorder: "#4f46e5",
    text: "text-indigo-800",
    pill: "bg-indigo-100 text-indigo-700",
  },
  {
    bg: "rgba(20,184,166,0.10)",
    border: "#14b8a6",
    selectedBg: "rgba(13,148,136,0.16)",
    selectedBorder: "#0d9488",
    text: "text-teal-800",
    pill: "bg-teal-100 text-teal-700",
  },
  {
    bg: "rgba(236,72,153,0.10)",
    border: "#ec4899",
    selectedBg: "rgba(219,39,119,0.16)",
    selectedBorder: "#db2777",
    text: "text-pink-800",
    pill: "bg-pink-100 text-pink-700",
  },
] as const;

export const FIELD_CONFIG: Record<
  FieldType,
  { icon: typeof PenLine; label: string; size: [number, number] }
> = {
  checkbox: { icon: CheckSquare, label: "Checkbox", size: [0.06, 0.05] },
  date: { icon: CalendarDays, label: "Date", size: [0.18, 0.05] },
  initials: { icon: FileSignature, label: "Initials", size: [0.18, 0.06] },
  signature: { icon: PenLine, label: "Signature", size: [0.3, 0.075] },
  text: { icon: Type, label: "Text", size: [0.28, 0.02] },
};

// PDF exports place text this far above the bottom of its signer field.
export const SIGNER_FIELD_TEXT_BASELINE_OFFSET = 6;

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function createField(
  type: FieldType,
  signerId: string,
  signerOrder?: number,
  page = 1,
  x = 0.1,
  y = 0.1,
  sizeOverride?: Pick<LayoutField, "height" | "width">,
): LayoutField {
  const config = FIELD_CONFIG[type];
  const width = sizeOverride?.width ?? config.size[0];
  const height = sizeOverride?.height ?? config.size[1];
  return {
    ...(type === "text" || type === "date"
      ? { fontFamily: "Times New Roman" as const, fontSize: 11 }
      : {}),
    height,
    id: crypto.randomUUID(),
    label: config.label,
    page,
    signerId,
    signerOrder,
    type,
    width,
    x,
    y,
  };
}
